import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { BeatLoader } from "react-spinners";
import { usePostHog } from "../../../hooks/usePostHog";
import ProgressBar from "../../../components/ProgressBar/ProgressBar";
import Timer from "./Timer";
import QuestionCard from "./QuestionCard";
import AnswerOption from "./AnswerOption";
import UploadComponent from "../../../components/common/UploadFile/UploadComponent";
import CommonModal from "../../../components/common/Modal/ModalCommon";
import Button from "../../../components/common/Button/Button";
import Slider from "../../../components/common/RangeSlider/Slider";
import images from "../../../images";
import {
  apiSlice,
  useGetTakeQuestQuery,
  // useLazyGetTakeQuestPracticeQuery,
  usePostAnswerMutation,
  usePostTakeQuestCompleteMutation,
  usePostQuestReviewMutation,
  usePostEndQuestMutation,
  usePostPauseTimerParticipantMutation,
  useGetQuestDetailsQuery,
  usePostSaveQuestMutation,
  useLazyGetTimeExtensionQuery,
  useLazyGetDetailParticipantQuery,
} from "../../../services/apiSlice";
import {
  cn,
  formatDateToFakeMicroUTC,
  isSelectedForRequiredQuest,
  renderWithBold,
} from "../../../Utils/helper";
import { QuestionType } from "../../../Utils/enum";
import HeaderQuestPratice from "./HeaderQuestPratice";
import ProgressDotGroup from "../../../components/ProgressBar/ProgressDotGroup";
import AnswerYesNoQuestion from "./AnswerYesNoQuestion";
import TimerTypeOclock from "./TimerTypeOclock";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";

const ANSWER_OPTION_TYPES = [QuestionType.MCQ, QuestionType.CHECKBOX];
const ANSWER_TEXT_TYPES = [QuestionType.SHORT, QuestionType.LONG];
const ANSWER_SLIDER_TYPES = [QuestionType.SLIDER];
const ANSWER_YESNO_TYPES = [QuestionType.YESNO];

const DetailMultipleChoice = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { trackEvent, trackTimer } = usePostHog();

  const questId = searchParams.get("quest_id");

  const expiredAtStrParam = searchParams.get("expired_at") ?? ""; // fallback rỗng nếu không có

  const [expiredAtTime, setExpiredAtTime] = useState(expiredAtStrParam);
  const [quotaType, setQuotaType] = useState("");

  const expiredAtStr = expiredAtTime;

  const now = new Date();
  const nowUTCISOString = formatDateToFakeMicroUTC(now);

  let expiredDate;

  try {
    if (expiredAtStr === "" || typeof expiredAtStr !== "string") {
      throw new Error("Invalid expiration time (not a string)");
    }

    const slicedStr = expiredAtStr.slice(0, 23);
    expiredDate = new Date(slicedStr);

    if (isNaN(expiredDate.getTime())) {
      throw new Error("Invalid date format");
    }
  } catch (error) {
    console.error("❌ Error parsing expiredAtTime:", error.message);
    expiredDate = new Date(); // fallback
  }
  const nowDate = new Date(nowUTCISOString.slice(0, 23));
  const diffMs = expiredDate.getTime() - nowDate.getTime();
  const diffSeconds = Math.round(diffMs / 1000);
  const validateDiffSeconds = diffSeconds <= 0 ? 0 : diffSeconds;

  const sttDone =
    searchParams.get("stt_done") === "null"
      ? null
      : parseInt(searchParams.get("stt_done"), 10);

  const [isPostAnswering, setIsPostAnswering] = useState(false);
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPrevStepLoading, setIsPrevStepLoading] = useState(false);
  const [submittedSteps, setSubmittedSteps] = useState([]);
  const [congratulation, setCongratulation] = useState(false);
  // const [isReviewingReceipt, setIsReviewingReceipt] = useState(false);

  const isPrevStepLoadingRef = useRef(false);
  const pauseRef = useRef();
  const endRef = useRef();
  const submitRef = useRef();
  const reviewingRef = useRef();
  const reviewCompleteRef = useRef();
  const reviewingCompleteRef = useRef();
  const outQuestModelRef = useRef();
  const timeIsRunningRef = useRef(true);
  const confirmSaveAndQuitRef = useRef();

  const modalRefs = useMemo(
    () => ({
      pause: pauseRef,
      end: endRef,
      submit: submitRef,
      reviewing: reviewingRef,
      reviewComplete: reviewCompleteRef,
      reviewingComplete: reviewingCompleteRef,
      outQuest: outQuestModelRef,
      timeIsRunning: timeIsRunningRef,
      confirmSaveAndQuit: confirmSaveAndQuitRef,
    }),
    []
  );

  const [state, setState] = useState({
    time: validateDiffSeconds,
    currentStep: sttDone && !isNaN(sttDone) ? Math.max(1, sttDone) : 1,
    selectedOption: { id: null, is_correct: null },
    selectedOptions: [],
    additionalComment: "",
    answerText: "",
    receiptFiles: [],
    fileAlreadyUploaded: false,
    isFromReview: false,
    questErrors: {},
    questSteps: {},
    questAnswers: {},
    inErrorReviewMode: false,
    currentErrorIndex: 0,
    errorQuestionsList: [],
    otherOptionTexts: {},
    isSubmitting: false,
    reviewMode: false,
    errorStepsArray: [],
    currentErrorStepIndex: 0,
    checkRunningTime: false,
  });
  const [hasOtherError, setHasOtherError] = useState(false);
  const [isReviewingDataResponse, setIsReviewingDataResponse] = useState(null);
  
  // Track quest session start time for abandonment tracking
  const [questSessionStartTime] = useState(Date.now());

  const { data, isLoading: isQuestLoading } = useGetTakeQuestQuery({
    quest_id: questId,
  });

  const [triggerTimeExtension, { isLoading: isTimeRunningLoading }] =
    useLazyGetTimeExtensionQuery();
  const [postAnswer] = usePostAnswerMutation();
  const [postQuestReview] = usePostQuestReviewMutation();
  // const [getTakeQuestPractice] = useLazyGetTakeQuestPracticeQuery();
  const [getDetailsPractice] = useLazyGetDetailParticipantQuery();

  const [postEndQuest] = usePostEndQuestMutation();
  const [postSaveQuest] = usePostSaveQuestMutation();

  const [postFileTakeQuestComplete] = usePostTakeQuestCompleteMutation();
  const [postPauseTimerParticipant] = usePostPauseTimerParticipantMutation();
  const { data: questDetailsData } = useGetQuestDetailsQuery(
    { quest_id: questId },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const thumbnailPaths =
    questDetailsData?.data.thumbnails?.map(
      (thumbnail) => thumbnail.file_path
    ) || [];

  const totalSteps = (data?.data?.length || 0) + 1;
  const currentStepData =
    state.currentStep > 1 && data?.data
      ? data.data[state.currentStep - 2]
      : null;

  // Track quest engagement on component mount
  useEffect(() => {
    if (questId && id) {
      trackEvent('quest_execution_started', {
        quest_id: questId,
        participant_id: id,
        total_steps: totalSteps,
        quota_type: quotaType,
        session_start_time: questSessionStartTime
      });
    }
  }, [questId, id, totalSteps, quotaType, trackEvent, questSessionStartTime]);

  // Track quest abandonment on component unmount or navigation
  useEffect(() => {
    const handleQuestAbandonment = () => {
      const timeSpentMs = Date.now() - questSessionStartTime;
      const timeSpentSeconds = Math.round(timeSpentMs / 1000);
      
      trackEvent('quest_abandoned', {
        quest_id: questId,
        participant_id: id,
        current_step: state.currentStep,
        total_steps: totalSteps,
        progress_percentage: Math.round((state.currentStep / totalSteps) * 100),
        time_spent_seconds: timeSpentSeconds,
        time_spent_minutes: Math.round(timeSpentSeconds / 60),
        abandonment_reason: 'page_unload',
        questions_answered: submittedSteps.length,
        quota_type: quotaType,
        remaining_time_seconds: state.time,
        quest_type: questDetailsData?.data?.quest_type?.name,
        business_name: questDetailsData?.data?.quest_business?.business?.name
      });
    };

    // Track page abandonment
    const handleBeforeUnload = (event) => {
      handleQuestAbandonment();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        const timeSpentMs = Date.now() - questSessionStartTime;
        trackEvent('quest_backgrounded', {
          quest_id: questId,
          participant_id: id,
          current_step: state.currentStep,
          time_spent_seconds: Math.round(timeSpentMs / 1000),
          progress_percentage: Math.round((state.currentStep / totalSteps) * 100)
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Track abandonment on unmount (navigation away)
      handleQuestAbandonment();
    };
  }, [questId, id, state.currentStep, totalSteps, state.time, submittedSteps.length, quotaType, questDetailsData, trackEvent, questSessionStartTime]);

  useEffect(() => {
    let timer;

    const updateTime = () => {
      // ✅ Bảo vệ khỏi lỗi khi expiredAtStr chưa sẵn sàng
      if (!expiredAtStr || typeof expiredAtStr !== "string") return;

      const now = new Date();
      const nowUTCISOString = formatDateToFakeMicroUTC(now);

      const expiredDate = new Date(expiredAtStr.slice(0, 23));
      const nowDate = new Date(nowUTCISOString.slice(0, 23));

      const diffMs = expiredDate.getTime() - nowDate.getTime();
      const diffSeconds = Math.max(0, Math.round(diffMs / 1000));

      setState((prev) => ({
        ...prev,
        time: diffSeconds,
      }));

      if (
        diffSeconds > 0 &&
        diffSeconds <= 30 &&
        quotaType === "SLOT" &&
        state.checkRunningTime === false
      ) {
        timeIsRunningRef.current?.openModal();
      } else if (diffSeconds <= 0) {
        timeIsRunningRef.current?.closeModal();
        outQuestModelRef.current?.openModal();
        clearInterval(timer);
      }
    };

    timer = setInterval(updateTime, 1000);
    updateTime();

    return () => clearInterval(timer);
  }, [questId, expiredAtStr, state.checkRunningTime, quotaType]);

  // useEffect(() => {
  //   let timer;
  //   const updateTime = () => {
  //     const now = new Date();
  //     const nowUTCISOString = formatDateToFakeMicroUTC(now);

  //     const expiredDate = new Date(expiredAtStr.slice(0, 23));
  //     const nowDate = new Date(nowUTCISOString.slice(0, 23));

  //     const diffMs = expiredDate.getTime() - nowDate.getTime();
  //     const diffSeconds = Math.max(0, Math.round(diffMs / 1000));
  //     setState((prev) => ({
  //       ...prev,
  //       time: diffSeconds,
  //     }));

  //     if (
  //       diffSeconds > 0 &&
  //       diffSeconds <= 30 &&
  //       state.checkRunningTime === false
  //     ) {
  //       timeIsRunningRef.current.openModal();
  //     } else if (diffSeconds <= 0) {
  //       timeIsRunningRef.current.closeModal();
  //       outQuestModelRef.current?.openModal();
  //       clearInterval(timer);
  //     }
  //   };

  //   timer = setInterval(updateTime, 1000);
  //   updateTime();

  //   return () => clearInterval(timer);
  // }, [questId, expiredAtStr, state.checkRunningTime]);

  useEffect(() => {
    const fetchQuest = async () => {
      setIsLoading(true);
      try {
        // if (quotaType === "SLOT") {
        const response = await getDetailsPractice({
          participant_id: id,
        }).unwrap();

        if (response.message === "Creation successful") {
          const selectedItem = response.data;
          if (selectedItem) {
            setExpiredAtTime(selectedItem.expired_at || null);
            setQuotaType(
              selectedItem?.quest?.quest_business?.quota_type ??
                questDetailsData?.data?.quest_business?.quota_type
            );
          } else {
            console.warn("No matching item found for ID:", id);
            setExpiredAtTime(null);
          }
          // setExpiredAtTime(response.data[0]?.expired_at || null);
          const questAnswers = {};
          const receiptLength = selectedItem?.receipt?.length || 0;
          if (receiptLength !== 0 && sttDone && !isNaN(sttDone)) {
            setState((prev) => ({
              ...prev,
              currentStep: Math.min(sttDone + 1, totalSteps),
            }));
          }
          const groupedAnswers = selectedItem?.quest_participant_answers.reduce(
            (acc, item) => {
              if (!acc[item.quest_question_id]) {
                acc[item.quest_question_id] = [];
              }
              acc[item.quest_question_id].push(item);
              return acc;
            },
            {}
          );

          Object.entries(groupedAnswers).forEach(([questionId, answers]) => {
            const questionData = data?.data?.find(
              (q) => String(q.id) === String(questionId)
            );
            const isCheckbox = questionData?.type === QuestionType.CHECKBOX;

            if (isCheckbox) {
              const optionIds = answers
                .map((a) => a.quest_question_option_id)
                .filter(Boolean);
              questAnswers[questionId] = {
                quest_question_id: questionId,
                quest_question_option_ids: optionIds,
                quest_question_option_id: optionIds[0] || null,
                is_correct: false,
                additional_comment: answers[0]?.additional_comment || "",
                answer_text:
                  answers.find((a) => !!a.answer_text)?.answer_text || "",
              };
            } else {
              const answer = answers[0];
              questAnswers[questionId] = {
                quest_question_id: questionId,
                quest_question_option_id:
                  answer.quest_question_option_id || null,
                quest_question_option_ids: answer.quest_question_option_id
                  ? [answer.quest_question_option_id]
                  : [],
                is_correct: answer.is_correct || false,
                additional_comment: answer.additional_comment || "",
                answer_text: answer.answer_text || "",
              };
            }
          });

          const otherOptionTexts = {};
          selectedItem?.quest_participant_answers.forEach((answer) => {
            const questionData = data?.data?.find(
              (q) => q.id === answer.quest_question_id
            );
            const selectedOption = questionData?.quest_question_option?.find(
              (opt) => opt.id === answer.quest_question_option_id
            );
            if (selectedOption?.is_other_option && answer.answer_text) {
              otherOptionTexts[answer.quest_question_id] = answer.answer_text;
            }
          });

          setState((prev) => ({
            ...prev,
            receiptFiles: selectedItem?.receipt || [],
            isFromReview: Object.keys(questAnswers).length > 0,
            questAnswers,
            otherOptionTexts,
            fileAlreadyUploaded: receiptLength > 0,
          }));
          // }
        }
      } catch (err) {
        console.error("Failed to fetch quest:", err);
        toast.error(t("toast.error.failedToFetchQuest"));
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuest();
  }, [
    questId,
    // getTakeQuestPractice,
    getDetailsPractice,
    data?.data,
    sttDone,
    totalSteps,
    t,
    navigate,
    questDetailsData?.data?.quest_business?.quota_type,
    id,
  ]);

  useEffect(() => {
    if (data?.data) {
      const questSteps = data.data.reduce(
        (acc, question, index) => ({
          ...acc,
          [question.id]: index + 2,
        }),
        {}
      );
      setState((prev) => ({
        ...prev,
        questSteps,
        currentStep: Math.min(prev.currentStep, totalSteps),
      }));
    }
  }, [data, totalSteps]);

  useEffect(() => {
    if (!currentStepData) return;

    const currentData = state.questAnswers?.[currentStepData?.id];
    let selectedOption = {
      id: currentData?.quest_question_option_id || null,
      is_correct: currentData?.is_correct || false,
    };

    let selectedOptions = [];
    if (
      currentStepData?.type === QuestionType.CHECKBOX &&
      currentData?.quest_question_option_ids?.length > 0
    ) {
      selectedOptions = currentData.quest_question_option_ids.map((optId) => {
        const option = currentStepData.quest_question_option?.find(
          (opt) => opt.id === optId
        );
        return {
          id: optId,
          is_correct: option?.is_correct || false,
        };
      });
    } else if (selectedOption.id) {
      selectedOptions = [selectedOption];
    }

    setState((prev) => ({
      ...prev,
      answerText: currentData?.answer_text || "",
      additionalComment: currentData?.additional_comment || "",
      selectedOption,
      selectedOptions,
    }));
  }, [state.questAnswers, state.currentStep, currentStepData]);

  const handleSelect = useCallback(
    (optionId, isCorrect) => {
      if (!currentStepData) return;
      const isCheckbox = currentStepData?.type === QuestionType.CHECKBOX;

      setState((prevState) => {
        let newSelectedOptions = [...prevState.selectedOptions];
        if (isCheckbox) {
          const isAlreadySelected = newSelectedOptions.some(
            (opt) => opt.id === optionId
          );
          if (isAlreadySelected) {
            newSelectedOptions = newSelectedOptions.filter(
              (opt) => opt.id !== optionId
            );
          } else {
            newSelectedOptions.push({ id: optionId, is_correct: isCorrect });
          }
        } else {
          newSelectedOptions = [{ id: optionId, is_correct: isCorrect }];
        }

        return {
          ...prevState,
          selectedOptions: newSelectedOptions,
          selectedOption: isCheckbox
            ? newSelectedOptions[0] || { id: null, is_correct: null }
            : { id: optionId, is_correct: isCorrect },
        };
      });
    },
    [currentStepData]
  );

  const handleFileUpload = useCallback((newFiles) => {
    if (!newFiles || newFiles.length === 0) {
      setState((prevState) => ({
        ...prevState,
        receiptFiles: [],
        fileAlreadyUploaded: false,
      }));
      return;
    }
    const hasNewFiles = newFiles.some((file) => file instanceof File);
    setState((prevState) => ({
      ...prevState,
      receiptFiles: newFiles,
      fileAlreadyUploaded: hasNewFiles ? false : prevState.fileAlreadyUploaded,
    }));
  }, []);

  const handleOtherTextChange = (text) => {
    if (!currentStepData) return;

    setState((prevState) => ({
      ...prevState,
      otherOptionTexts: {
        ...prevState.otherOptionTexts,
        [currentStepData.id]: text,
      },
    }));
  };

  const handleOtherErrorChange = (hasError) => {
    setHasOtherError(hasError);
  };

  const saveCurrentAnswer = useCallback(async () => {
    if (!currentStepData) return;
    if (currentStepData.type === QuestionType.CHECKBOX) {
      const selectedCount = state.selectedOptions.length;
      // const requiredCount =
      //   currentStepData?.minimum_selection_quantity || state.maxCheckboxOptions;

      const requiredCount = currentStepData?.minimum_selection_quantity;

      if (
        requiredCount !== null &&
        requiredCount !== undefined &&
        requiredCount !== 0
      ) {
        if (selectedCount !== requiredCount) {
          if (selectedCount < requiredCount) {
            toast.error(
              t("toast.error.pleaseSelectExactlyOptions", {
                count: requiredCount,
                selected: selectedCount,
                type: "exactly",
              }) ||
                `Please select exactly ${requiredCount} options. You have selected ${selectedCount}.`
            );
          } else {
            toast.error(
              t("toast.error.pleaseSelectOnlyOptions", {
                count: requiredCount,
                selected: selectedCount,
              }) ||
                `Please select only ${requiredCount} options. You have selected ${selectedCount}.`
            );
          }
          throw new Error(
            `Validation failed: Need exactly ${requiredCount} checkbox options`
          );
        }
      }
    }
    const isAnswerInvalid =
      (ANSWER_TEXT_TYPES.includes(currentStepData.type) &&
        currentStepData.type === "long" &&
        (state.answerText || "").trim().length < 100) ||
      (ANSWER_TEXT_TYPES.includes(currentStepData.type) &&
        currentStepData.type === "short" &&
        (state.answerText || "").trim().length < 1) ||
      (currentStepData.is_additional_comment &&
        (state.additionalComment || "").trim().length < 50);

    if (isAnswerInvalid) {
      toast.error(t("toast.error.thisQuestIsRequired"));
      throw new Error("Validation failed: Answer invalid");
    }

    if (
      currentStepData?.is_required &&
      !isSelectedForRequiredQuest(
        currentStepData.type,
        state.selectedOption?.id,
        state.answerText
      )
    ) {
      toast.error(t("toast.error.thisQuestIsRequired"));
      throw new Error("Validation failed: Required question");
    }

    if (
      currentStepData?.is_additional_comment &&
      currentStepData.is_required_additional_comment &&
      !state.additionalComment
    ) {
      toast.error(t("quest.detailMultipleChoice.compulsoryReMarkRequired"));
      throw new Error("Validation failed: Compulsory re-mark required");
    }

    if (hasOtherError) {
      throw new Error("Validation failed: Other option has an error");
    }

    const selectedOptionData = currentStepData?.quest_question_option?.find(
      (option) => option.id === state.selectedOption?.id
    );
    const isOtherOptionSelected = selectedOptionData?.is_other_option;
    const optionIds =
      currentStepData?.type === QuestionType.CHECKBOX
        ? state.selectedOptions.map((opt) => opt.id).filter(Boolean)
        : state.selectedOption?.id
        ? [state.selectedOption.id]
        : [];
    const body = {
      quest_question_id: currentStepData?.id,
      quest_question_option_id: state.selectedOption?.id || null,
      quest_question_option_ids: optionIds,
      quest_participant_id: id,
      answer_text: isOtherOptionSelected
        ? state.otherOptionTexts[currentStepData.id] || ""
        : state.answerText || null,
      is_correct: state.selectedOption?.is_correct || false,
      additional_comment: state.additionalComment,
    };

    const response = await postAnswer(body).unwrap();
    return { response, savedAnswer: body };
  }, [
    currentStepData,
    state.answerText,
    state.additionalComment,
    state.selectedOption,
    state.selectedOptions,
    state.otherOptionTexts,
    hasOtherError,
    id,
    postAnswer,
    t,
  ]);

  const updateStateAfterSave = useCallback((savedAnswer) => {
    setState((prev) => ({
      ...prev,
      questAnswers: {
        ...prev.questAnswers,
        [savedAnswer.quest_question_id]: {
          quest_question_id: savedAnswer.quest_question_id,
          quest_question_option_id: savedAnswer.quest_question_option_id,
          quest_question_option_ids: savedAnswer.quest_question_option_ids,
          is_correct: savedAnswer.is_correct,
          additional_comment: savedAnswer.additional_comment,
          answer_text: savedAnswer.answer_text,
        },
      },
    }));
  }, []);

  const handleReview = useCallback(async () => {
    try {
      const { savedAnswer } = await saveCurrentAnswer();
      updateStateAfterSave(savedAnswer);
      setSubmittedSteps((prev) => {
        const stepIndex = state.currentStep - 1;
        return !prev.includes(stepIndex) ? [...prev, stepIndex] : prev;
      });
    } catch (error) {
      console.error("Validation/Save failed for the final answer:", error);
      return;
    }

    const unansweredQuestions = [];
    if (data?.data) {
      data.data.forEach((question, index) => {
        if (state.currentStep - 2 === index) {
          return;
        }

        const stepIndexForCheck = index + 1;
        const isSubmitted = submittedSteps.includes(stepIndexForCheck);
        const hasStoredAnswer = !!state.questAnswers[question.id];

        if (!isSubmitted && !hasStoredAnswer) {
          unansweredQuestions.push({ questionNo: question.no });
        }
      });
    }

    if (unansweredQuestions.length > 0) {
      const questionNumbers = unansweredQuestions
        .map((q) => q.questionNo)
        .join(", ");
      toast.error(
        `${t("toast.error.pleaseAnswerAllQuestions")} ${questionNumbers}`
      );
      return;
    }

    reviewingRef.current?.openModal();
    
    // Track quest review started
    const questReviewTimer = trackTimer('quest_review_duration');
    trackEvent('quest_review_started', {
      quest_id: questId,
      participant_id: id,
      total_questions: data?.data?.length || 0,
      unanswered_questions: unansweredQuestions.length,
      submitted_steps: submittedSteps.length
    });
    
    try {
      const responseAnswer = await postQuestReview({
        participant_id: id,
      }).unwrap();
      if (responseAnswer.status === 200) {
        setIsReviewingDataResponse(responseAnswer?.data);
        if (
          responseAnswer?.data?.add_token === 0 ||
          responseAnswer?.data?.add_xp === 0
        ) {
          setCongratulation(false);
        } else {
          setCongratulation(true);
        }
        const questErrors =
          responseAnswer?.data?.missing_or_not_correct_questions?.reduce(
            (acc, item) => {
              const stepIndex = state.questSteps[item.question_id] || 2;
              acc[item.question_id] = { ...item, step_index: stepIndex };
              return acc;
            },
            {}
          );
        // setIsReviewingReceipt(responseAnswer?.data?.is_pass_review_receipt);
        setState((prevState) => ({ ...prevState, questErrors }));
        reviewingRef.current?.closeModal();

        const hasQuestErrors =
          questErrors && Object.keys(questErrors).length > 0;

        console.log("hasQuestErrors:", hasQuestErrors);
        console.log(
          "responseAnswer?.data?.is_pass_review_receipt",
          responseAnswer?.data?.is_pass_review_receipt
        );

        if (
          hasQuestErrors === false &&
          responseAnswer?.data?.is_pass_review_receipt === false
        ) {
          try {
            questReviewTimer.end({
              success: true,
              has_errors: false,
              pass_review_receipt: false
            });

            // Track quest completion attempt
            const completionTimer = trackTimer('quest_completion_duration');
            trackEvent('quest_completion_started', {
              quest_id: questId,
              participant_id: id,
              coins_earned: isReviewingDataResponse?.add_token ?? 0,
              xp_earned: isReviewingDataResponse?.add_xp ?? 0,
              bonus_xp: isReviewingDataResponse?.bonus_xp ?? 0,
              is_completed: isReviewingDataResponse?.is_completed ?? false
            });

            const response = await postFileTakeQuestComplete({
              quest_id: id,
              xp: isReviewingDataResponse?.add_xp ?? 0,
              bonus_xp: isReviewingDataResponse?.bonus_xp ?? 0,
              token: isReviewingDataResponse?.add_token ?? "0",
              is_completed: isReviewingDataResponse?.is_completed ?? false,
            }).unwrap();

            if (response.message === "Creation successful") {
              completionTimer.end({
                success: true,
                message: response.message
              });

              trackEvent('quest_completed', {
                quest_id: questId,
                participant_id: id,
                coins_earned: isReviewingDataResponse?.add_token ?? 0,
                xp_earned: isReviewingDataResponse?.add_xp ?? 0,
                bonus_xp: isReviewingDataResponse?.bonus_xp ?? 0,
                completion_status: isReviewingDataResponse?.add_token > 0 ? 'approved' : 'under_review',
                total_questions: data?.data?.length || 0,
                submitted_steps: submittedSteps.length,
                slot_left: questDetailsData?.data?.slot_available
              });

              localStorage.removeItem(`timer_${questId}`);
              localStorage.removeItem(`initialTime_${questId}`);
              navigate(`/quest/details/completed`, {
                state: {
                  responseAnswer: response,
                  questReview: isReviewingDataResponse,
                  slotLeft: questDetailsData?.data?.slot_available,
                },
              });
            }
          } catch (err) {
            if (err.data?.detail === "You already complete this quest") {
              toast.warning(t("toast.warning.youAlreadyCompleteThisQuest"));
              navigate("/");
              localStorage.removeItem(`timer_${questId}`);
              localStorage.removeItem(`initialTime_${questId}`);
            }
          }
        }
        if (responseAnswer?.data?.is_completed === true) {
          try {
            const response = await postFileTakeQuestComplete({
              quest_id: id,
              xp: responseAnswer?.data?.add_xp ?? 0,
              bonus_xp: responseAnswer?.data?.bonus_xp ?? 0,
              token: responseAnswer?.data?.add_token ?? "0",
              is_completed: responseAnswer?.data?.is_completed ?? false,
            }).unwrap();
            if (response.message === "Creation successful") {
              navigate(`/quest/details/completed`, {
                state: {
                  responseAnswer: response,
                  questReview: responseAnswer?.data,
                  slotLeft: questDetailsData?.data?.slot_available,
                },
              });
            }
          } catch (err) {
            if (err.data?.detail === "You already complete this quest") {
              toast.warning(t("toast.warning.youAlreadyCompleteThisQuest"));
              navigate("/");
            }
          }
        } else if (hasQuestErrors) {
          submitRef.current?.closeModal();
          reviewCompleteRef.current?.openModal();
        } else {
          submitRef.current?.closeModal();
          reviewCompleteRef.current?.openModal();
        }
      }
    } catch (err) {
      reviewingRef.current?.closeModal();
      toast.error(t("toast.error.reviewFailed"));
    }
  }, [
    data?.data,
    state.currentStep,
    submittedSteps,
    state.questAnswers,
    saveCurrentAnswer,
    id,
    postQuestReview,
    navigate,
    // questId,
    postFileTakeQuestComplete,
    questDetailsData?.data?.slot_available,
    state.questSteps,
    t,
    updateStateAfterSave,
    isReviewingDataResponse,
    questId,
    trackEvent,
    trackTimer,
  ]);

  const handleNextStep = useCallback(async () => {
    if (isPostAnswering) return;

    if (state.currentStep === 1) {
      trackEvent('quest_question_answered', {
        quest_id: questId,
        question_step: 0,
        question_type: 'file_upload',
        is_file_upload: true,
        progress_percentage: Math.round((1 / totalSteps) * 100)
      });

      setSubmittedSteps((prev) => (!prev.includes(0) ? [...prev, 0] : prev));
      setState((prevState) => ({
        ...prevState,
        currentStep: Math.min(prevState.currentStep + 1, totalSteps),
        fileAlreadyUploaded: true,
      }));
      return;
    }
    if (currentStepData && currentStepData.type === QuestionType.CHECKBOX) {
      const selectedCount = state.selectedOptions.length;
      // const requiredCount =
      //   currentStepData?.minimum_selection_quantity || state.maxCheckboxOptions;
      const requiredCount = currentStepData?.minimum_selection_quantity;
      if (
        requiredCount !== null &&
        requiredCount !== undefined &&
        requiredCount !== 0
      ) {
        if (selectedCount !== requiredCount) {
          if (selectedCount < requiredCount) {
            toast.error(
              t("toast.error.pleaseSelectExactlyOptions", {
                count: requiredCount,
                selected: selectedCount,
              })
            );
          } else {
            toast.error(
              t("toast.error.pleaseSelectOnlyOptions", {
                count: requiredCount,
                selected: selectedCount,
              })
            );
          }
          return;
        }
      }
    }
    setIsPostAnswering(true);
    try {
      const answerStartTime = Date.now();
      const { savedAnswer } = await saveCurrentAnswer();
      
      // Track question answered
      trackEvent('quest_question_answered', {
        quest_id: questId,
        question_step: state.currentStep,
        question_type: currentStepData?.type,
        question_id: currentStepData?.id,
        is_required: currentStepData?.required || false,
        answer_time_ms: Date.now() - answerStartTime,
        progress_percentage: Math.round((state.currentStep / totalSteps) * 100),
        review_mode: state.reviewMode,
        selected_options_count: state.selectedOptions.length,
        has_text_answer: !!(state.answerText || state.additionalComment)
      });

      updateStateAfterSave(savedAnswer);
      setSubmittedSteps((prev) => {
        const stepIndex = state.currentStep - 1;
        if (!prev.includes(stepIndex)) {
          return [...prev, stepIndex];
        }
        return prev;
      });

      if (state.reviewMode && state.errorStepsArray.length > 0) {
        const currentErrorIndex = state.currentErrorStepIndex;
        const isLastErrorStep =
          currentErrorIndex === state.errorStepsArray.length - 1;

        if (isLastErrorStep) {
          await handleReview();
        } else {
          const nextErrorIndex = currentErrorIndex + 1;
          const nextErrorStep = state.errorStepsArray[nextErrorIndex];
          setState((prev) => ({
            ...prev,
            currentStep: nextErrorStep,
            currentErrorStepIndex: nextErrorIndex,
            answerText: "",
            additionalComment: "",
            selectedOption: { id: null, is_correct: null },
            selectedOptions: [],
          }));
          setTimeout(() => window.scrollTo(0, 0), 10);
        }
      } else if (state.currentStep >= totalSteps) {
        await handleReview();
      } else {
        let nextStep = state.currentStep + 1;
        if (state.reviewMode) {
          const errorSteps = state.errorStepsArray.filter(
            (step) => step > state.currentStep
          );
          nextStep = errorSteps.length > 0 ? errorSteps[0] : totalSteps;
          const nextErrorIndex = state.errorStepsArray.indexOf(nextStep);
          setState((prev) => ({
            ...prev,
            currentStep: nextStep,
            currentErrorStepIndex:
              nextErrorIndex >= 0 ? nextErrorIndex : prev.currentErrorStepIndex,
            answerText: "",
            additionalComment: "",
            selectedOption: { id: null, is_correct: null },
            selectedOptions: [],
          }));
        } else {
          setState((prev) => ({
            ...prev,
            currentStep: nextStep,
            answerText: "",
            additionalComment: "",
            selectedOption: { id: null, is_correct: null },
            selectedOptions: [],
          }));
        }
        setTimeout(() => window.scrollTo(0, 0), 10);
      }
    } catch (error) {
      console.error("Error in handleNextStep:", error.message);
    } finally {
      setIsPostAnswering(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPostAnswering,
    state.currentStep,
    state.reviewMode,
    state.errorStepsArray,
    state.currentErrorStepIndex,
    totalSteps,
    saveCurrentAnswer,
    handleReview,
    updateStateAfterSave,
    state.selectedOptions,
    t,
    currentStepData,
    questId,
    state.answerText,
    state.additionalComment,
    trackEvent,
    trackTimer,
  ]);

  // const handlePrevStep = useCallback(() => {
  //   if (isPrevStepLoadingRef.current) return;
  //   isPrevStepLoadingRef.current = true;
  //   setIsPrevStepLoading(true);

  //   try {
  //     if (state.reviewMode && state.errorStepsArray.length > 0) {
  //       const prevErrorIndex = state.currentErrorStepIndex - 1;
  //       if (prevErrorIndex >= 0) {
  //         setState((prev) => ({
  //           ...prev,
  //           currentErrorStepIndex: prevErrorIndex,
  //           currentStep: state.errorStepsArray[prevErrorIndex],
  //         }));
  //       } else {
  //         const newStep = Math.max(state.currentStep - 1, 2);
  //         setState((prev) => ({
  //           ...prev,
  //           currentStep: newStep,
  //           reviewMode: false,
  //           currentErrorStepIndex: 0,
  //         }));
  //       }
  //     } else {
  //       const newStep = Math.max(state.currentStep - 1, 2);
  //       setState((prev) => ({
  //         ...prev,
  //         currentStep: newStep,
  //         isFromReview: false,
  //       }));
  //     }

  //     setTimeout(() => {
  //       const contentContainer = document.querySelector(
  //         ".flex-1.overflow-y-auto"
  //       );
  //       if (contentContainer) {
  //         contentContainer.scrollTop = 0;
  //       }
  //       window.scrollTo(0, 0);
  //     }, 10);
  //   } catch (error) {
  //     console.error("Error in handlePrevStep:", error);
  //   } finally {
  //     setTimeout(() => {
  //       isPrevStepLoadingRef.current = false;
  //       setIsPrevStepLoading(false);
  //     }, 500);
  //   }
  // }, [
  //   state.reviewMode,
  //   state.errorStepsArray,
  //   state.currentErrorStepIndex,
  //   state.currentStep,
  // ]);

  const handlePrevStep = useCallback(async () => {
    if (isPrevStepLoadingRef.current) return;
    isPrevStepLoadingRef.current = true;
    setIsPrevStepLoading(true);

    try {
      // Check and validate checkbox type questions
      if (currentStepData && currentStepData.type === QuestionType.CHECKBOX) {
        const selectedCount = state.selectedOptions.length;
        const requiredCount = currentStepData?.minimum_selection_quantity;
        if (
          requiredCount !== null &&
          requiredCount !== undefined &&
          requiredCount !== 0
        ) {
          if (selectedCount !== requiredCount) {
            if (selectedCount < requiredCount) {
              toast.error(
                t("toast.error.pleaseSelectExactlyOptions", {
                  count: requiredCount,
                  selected: selectedCount,
                })
              );
            } else {
              toast.error(
                t("toast.error.pleaseSelectOnlyOptions", {
                  count: requiredCount,
                  selected: selectedCount,
                })
              );
            }
            isPrevStepLoadingRef.current = false;
            setIsPrevStepLoading(false);
            return;
          }
        }
      }

      // Save current answer before navigating
      if (state.currentStep !== 1) {
        // Skip saving for step 1 as in handleNextStep
        const { savedAnswer } = await saveCurrentAnswer();
        updateStateAfterSave(savedAnswer);
        setSubmittedSteps((prev) => {
          const stepIndex = state.currentStep - 1;
          if (!prev.includes(stepIndex)) {
            return [...prev, stepIndex];
          }
          return prev;
        });
      }

      // Navigate to previous step
      if (state.reviewMode && state.errorStepsArray.length > 0) {
        const prevErrorIndex = state.currentErrorStepIndex - 1;
        if (prevErrorIndex >= 0) {
          setState((prev) => ({
            ...prev,
            currentErrorStepIndex: prevErrorIndex,
            currentStep: state.errorStepsArray[prevErrorIndex],
            answerText: "",
            additionalComment: "",
            selectedOption: { id: null, is_correct: null },
            selectedOptions: [],
          }));
        } else {
          const newStep = Math.max(state.currentStep - 1, 2);
          setState((prev) => ({
            ...prev,
            currentStep: newStep,
            reviewMode: false,
            currentErrorStepIndex: 0,
            answerText: "",
            additionalComment: "",
            selectedOption: { id: null, is_correct: null },
            selectedOptions: [],
          }));
        }
      } else {
        const newStep = Math.max(state.currentStep - 1, 2);
        setState((prev) => ({
          ...prev,
          currentStep: newStep,
          isFromReview: false,
          answerText: "",
          additionalComment: "",
          selectedOption: { id: null, is_correct: null },
          selectedOptions: [],
        }));
      }

      setTimeout(() => {
        const contentContainer = document.querySelector(
          ".flex-1.overflow-y-auto"
        );
        if (contentContainer) {
          contentContainer.scrollTop = 0;
        }
        window.scrollTo(0, 0);
      }, 10);
    } catch (error) {
      console.error("Error in handlePrevStep:", error);
    } finally {
      setTimeout(() => {
        isPrevStepLoadingRef.current = false;
        setIsPrevStepLoading(false);
      }, 500);
    }
  }, [
    state.reviewMode,
    state.errorStepsArray,
    state.currentErrorStepIndex,
    state.currentStep,
    state.selectedOptions,
    currentStepData,
    t,
    saveCurrentAnswer,
    updateStateAfterSave,
  ]);
  const handleComplete = async () => {
    if (state.receiptFiles.length === 0 || state.currentStep === 1) {
      toast.error(
        t("toast.error.pleaseUploadAtLeastOneFileInStep1BeforeSubmitting")
      );
      setState((prev) => ({
        ...prev,
        currentStep: 1,
        isFromReview: true,
      }));
      return;
    }

    reviewCompleteRef.current?.closeModal();
    submitRef.current?.closeModal();

    try {
      const response = await postFileTakeQuestComplete({
        quest_id: id,
        xp: isReviewingDataResponse?.add_xp ?? 0,
        bonus_xp: isReviewingDataResponse?.bonus_xp ?? 0,
        token: isReviewingDataResponse?.add_token ?? "0",
        is_completed: isReviewingDataResponse?.is_completed ?? false,
      }).unwrap();

      if (response.message === "Creation successful") {
        localStorage.removeItem(`timer_${questId}`);
        localStorage.removeItem(`initialTime_${questId}`);
        navigate(`/quest/details/completed`, {
          state: {
            responseAnswer: response,
            questReview: isReviewingDataResponse,
            slotLeft: questDetailsData?.data?.slot_available,
          },
        });
      }
    } catch (err) {
      if (err.data?.detail === "You already complete this quest") {
        toast.warning(t("toast.warning.youAlreadyCompleteThisQuest"));
        navigate("/");
        localStorage.removeItem(`timer_${questId}`);
        localStorage.removeItem(`initialTime_${questId}`);
      }
    }
  };

  const handlePauseTakeQuest = useCallback(async () => {
    try {
      const timeSpentMs = Date.now() - questSessionStartTime;
      const minutesLeft = state.time / 60;

      // Track quest pause attempt
      trackEvent('quest_paused', {
        quest_id: questId,
        participant_id: id,
        current_step: state.currentStep,
        total_steps: totalSteps,
        progress_percentage: Math.round((state.currentStep / totalSteps) * 100),
        time_spent_seconds: Math.round(timeSpentMs / 1000),
        time_remaining_seconds: state.time,
        time_remaining_minutes: minutesLeft,
        questions_answered: submittedSteps.length,
        pause_reason: 'user_initiated'
      });

      await postPauseTimerParticipant({
        participant_id: id,
        minutes_left: minutesLeft,
      }).unwrap();

      navigate(`/quest/details/${questId}`);

      dispatch(
        apiSlice.util.invalidateTags([
          { type: "QuestDetails", id: `Detail_${questId}` },
        ])
      );
    } catch (error) {
      console.error("Failed to pause quest:", error);
      toast.error(t("toast.error.failedToPauseQuest"));
      
      trackEvent('quest_pause_failed', {
        quest_id: questId,
        participant_id: id,
        error_message: error.message || 'Unknown error'
      });
    }
  }, [
    navigate,
    questId,
    dispatch,
    state.time,
    state.currentStep,
    totalSteps,
    submittedSteps.length,
    id,
    postPauseTimerParticipant,
    trackEvent,
    questSessionStartTime,
    t,
  ]);

  const handleEndQuest = useCallback(async () => {
    try {
      const timeSpentMs = Date.now() - questSessionStartTime;

      // Track quest quit/end attempt
      trackEvent('quest_quit', {
        quest_id: questId,
        participant_id: id,
        current_step: state.currentStep,
        total_steps: totalSteps,
        progress_percentage: Math.round((state.currentStep / totalSteps) * 100),
        time_spent_seconds: Math.round(timeSpentMs / 1000),
        time_remaining_seconds: state.time,
        questions_answered: submittedSteps.length,
        quit_reason: 'user_initiated'
      });

      const response = await postEndQuest({
        participant_id: id,
      }).unwrap();
      if (
        response.message === "Creation successful" ||
        response.status === 200
      ) {
        localStorage.removeItem(`timer_${questId}`);
        localStorage.removeItem(`initialTime_${questId}`);
        dispatch(
          apiSlice.util.invalidateTags([
            "QuestList",
            { type: "QuestDetails", id: `Detail_${questId}` },
          ])
        );
        toast.success(response?.data.message);
        navigate(`/quest/details/${questId}`);
      }
    } catch (error) {
      console.error("Failed to end quest:", error);
      toast.error("Failed to end quest");
      
      trackEvent('quest_quit_failed', {
        quest_id: questId,
        participant_id: id,
        error_message: error.message || 'Unknown error'
      });
    }
  }, [id, questId, navigate, postEndQuest, dispatch, state.currentStep, totalSteps, state.time, submittedSteps.length, trackEvent, questSessionStartTime]);

  const handleSaveQuest = useCallback(async () => {
    try {
      const timeSpentMs = Date.now() - questSessionStartTime;

      // Track quest save attempt
      trackEvent('quest_saved_and_quit', {
        quest_id: questId,
        participant_id: id,
        current_step: state.currentStep,
        total_steps: totalSteps,
        progress_percentage: Math.round((state.currentStep / totalSteps) * 100),
        time_spent_seconds: Math.round(timeSpentMs / 1000),
        time_remaining_seconds: state.time,
        questions_answered: submittedSteps.length,
        save_reason: 'user_initiated'
      });

      const response = await postSaveQuest({
        participant_id: id,
      }).unwrap();

      if (response.status === 422) {
        try {
          const response = await postEndQuest({
            participant_id: id,
          }).unwrap();
          if (
            response.message === "Creation successful" ||
            response.status === 200
          ) {
            localStorage.removeItem(`timer_${questId}`);
            localStorage.removeItem(`initialTime_${questId}`);
            dispatch(
              apiSlice.util.invalidateTags([
                "QuestList",
                {
                  type: "QuestDetails",
                  id: `Detail_${questId}`,
                },
              ])
            );
            toast.success(response?.data.message);
            navigate(`/quest/details/${questId}`);
          }
        } catch (error) {
          console.error("Failed to end quest:", error);
        }
      } else if (
        response.message === "Creation successful" ||
        response.status === 200
      ) {
        localStorage.removeItem(`timer_${questId}`);
        localStorage.removeItem(`initialTime_${questId}`);
        navigate(`/`);
      }
    } catch (error) {
      console.error("Failed to save quest:", error);
      
      trackEvent('quest_save_failed', {
        quest_id: questId,
        participant_id: id,
        error_message: error.message || 'Unknown error'
      });
    }
  }, [id, navigate, postSaveQuest, dispatch, postEndQuest, questId, state.currentStep, totalSteps, state.time, submittedSteps.length, trackEvent, questSessionStartTime]);

  const handleBackQuest = useCallback(() => {
    if (
      isReviewingDataResponse?.ocr_receipt_job === false ||
      isReviewingDataResponse?.is_location === false ||
      isReviewingDataResponse?.is_time === false
    ) {
      setState((prev) => ({ ...prev, currentStep: 1 }));
      modalRefs.reviewComplete.current?.closeModal();
      return;
    }

    const errorQuestions = Object.values(state.questErrors);
    if (errorQuestions.length === 0) {
      toast.error(t("toast.error.noErrorsToReview"));
      modalRefs.reviewComplete.current?.closeModal();
      return;
    }

    const errorStepsArray = Object.values(state.questErrors || {})
      .map((error) => error.step_index)
      .filter((step) => typeof step === "number" && !isNaN(step))
      .sort((a, b) => a - b);
    const sortedErrors = errorQuestions.sort(
      (a, b) => (a.no || 0) - (b.no || 0)
    );

    setSubmittedSteps([]);

    setState((prev) => ({
      ...prev,
      inErrorReviewMode: true,
      currentErrorIndex: 0,
      errorQuestionsList: sortedErrors,
      reviewMode: true,
      errorStepsArray,
      currentErrorStepIndex: 0,
      currentStep:
        errorStepsArray.length > 0 ? errorStepsArray[0] : prev.currentStep,
    }));

    modalRefs.reviewComplete.current?.closeModal();
  }, [isReviewingDataResponse, state.questErrors, t, modalRefs]);

  const sortedOptions = useCallback((options = []) => {
    const otherOptions = [];
    const normalOptions = [];

    options.forEach((option) => {
      const text = String(option.option_text || "").toLowerCase();

      if (text === "other" || text === "others" || option.is_other_option) {
        otherOptions.push(option);
      } else {
        normalOptions.push(option);
      }
    });

    normalOptions.sort((a, b) => {
      const valueA = Number(a.value) || 0;
      const valueB = Number(b.value) || 0;
      return valueA - valueB;
    });

    otherOptions.sort((a, b) => {
      const valueA = Number(a.value) || 999;
      const valueB = Number(b.value) || 999;
      return valueA - valueB;
    });

    return [...normalOptions, ...otherOptions];
  }, []);

  const handleStepChange = useCallback(
    async (newStep, options = {}) => {
      if (options.skipValidation) {
        setState((prev) => ({
          ...prev,
          currentStep: newStep + 1,
        }));
        return;
      }

      // const hasStoredAnswer = currentStepData?.id
      //   ? !!state.questAnswers[currentStepData.id]
      //   : false;

      const hasCurrentInput =
        (state.selectedOption?.id && state.selectedOption.id !== null) ||
        (state.selectedOptions && state.selectedOptions.length > 0) ||
        (state.answerText && state.answerText.trim()) ||
        (state.additionalComment && state.additionalComment.trim()) ||
        (currentStepData?.id &&
          state.otherOptionTexts[currentStepData.id] &&
          state.otherOptionTexts[currentStepData.id].trim());

      if (currentStepData && currentStepData.type === QuestionType.CHECKBOX) {
        const selectedCount = state.selectedOptions.length;
        // const requiredCount =
        //   currentStepData?.minimum_selection_quantity || state.maxCheckboxOptions;
        const requiredCount = currentStepData?.minimum_selection_quantity;
        if (
          requiredCount !== null &&
          requiredCount !== undefined &&
          requiredCount !== 0
        ) {
          if (selectedCount !== requiredCount) {
            if (selectedCount < requiredCount) {
              toast.error(
                t("toast.error.pleaseSelectExactlyOptions", {
                  count: requiredCount,
                  selected: selectedCount,
                })
              );
            } else {
              toast.error(
                t("toast.error.pleaseSelectOnlyOptions", {
                  count: requiredCount,
                  selected: selectedCount,
                })
              );
            }
            return;
          }
        }
      }
      if (hasCurrentInput === undefined) {
        toast.error(t("toast.error.thisQuestIsRequired"));
        return;
      }
      if (state.currentStep !== 1 && hasCurrentInput && !isPostAnswering) {
        setIsPostAnswering(true);
        try {
          const { savedAnswer } = await saveCurrentAnswer();
          updateStateAfterSave(savedAnswer);
          setSubmittedSteps((prev) => {
            const stepIndex = state.currentStep - 1;
            return !prev.includes(stepIndex) ? [...prev, stepIndex] : prev;
          });
        } catch (error) {
          console.error("Error saving answer in handleStepChange:", error);
          return;
          // if (!error.message.includes("Validation failed")) {
          //   setIsPostAnswering(false);
          //   return;
          // }
        } finally {
          setIsPostAnswering(false);
        }
      }

      setState((prev) => ({
        ...prev,
        currentStep: newStep + 1,
        isFromReview: false,
      }));

      setTimeout(() => {
        const contentContainer = document.querySelector(
          ".flex-1.overflow-y-auto"
        );
        if (contentContainer) {
          contentContainer.scrollTop = 0;
        }
        window.scrollTo(0, 0);
      }, 10);
    },
    [
      currentStepData,
      state.selectedOption,
      state.selectedOptions,
      state.answerText,
      state.additionalComment,
      state.otherOptionTexts,
      isPostAnswering,
      saveCurrentAnswer,
      updateStateAfterSave,
      state.currentStep,

      t,
    ]
  );

  const handleDropReportClick = useCallback(async () => {
    try {
      const minutesLeft = state.time / 60;

      await postPauseTimerParticipant({
        participant_id: id,
        minutes_left: minutesLeft,
      }).unwrap();

      dispatch(
        apiSlice.util.invalidateTags([
          { type: "QuestDetails", id: `Detail_${questId}` },
        ])
      );

      navigate("/profile/support/contactUS");
    } catch (error) {
      console.error("Failed to pause quest before report:", error);
      toast.error(t("toast.error.failedToPauseQuest"));

      navigate("/profile/support/contactUS");
    }
  }, [
    state.time,
    id,
    postPauseTimerParticipant,
    dispatch,
    questId,
    navigate,
    t,
  ]);

  const renderQuestionContent = () => {
    if (state.currentStep === 1) {
      return (
        <div className="flex flex-col mt-12 w-full">
          <h4 className="text-2xl font-bold leading-tight text-[#253FF6]">
            {t("quest.detailMultipleChoice.uploadReceipt")}
          </h4>
          <span className="text-base font-normal leading-tight text-[#A0A0A0] mt-2">
            {" "}
            {renderWithBold(
              t("quest.detailMultipleChoice.uploadReceiptDescription")
            )}
          </span>
          <div>
            <UploadComponent
              label={t("base.upload")}
              onFileUpload={handleFileUpload}
              maxFiles={1}
              participantId={id}
              initialFiles={state.receiptFiles}
              handleNextStep={handleNextStep}
              setFileUploaded={(value) =>
                setState((prev) => ({
                  ...prev,
                  fileAlreadyUploaded: value,
                }))
              }
            />
          </div>
        </div>
      );
    }

    if (isLoading || isQuestLoading || !currentStepData) {
      return (
        <div className="flex flex-col items-center justify-center mt-12 w-full">
          <BeatLoader size={10} color="#253FF6" />
          <span className="mt-4 text-base text-[#A0A0A0]">
            {t("base.loading")}
          </span>
        </div>
      );
    }
    const selectedValue = currentStepData?.quest_question_option?.find(
      (item) => item.id === state.selectedOption?.id
    )?.value;

    return (
      <div className="flex flex-col mt-8 w-full px-4">
        <QuestionCard
          text={currentStepData.question}
          stt={currentStepData.no}
          typeQuestion={currentStepData.type}
          requiredOption={currentStepData.minimum_selection_quantity}
          questReviewErrors={state.questErrors[currentStepData.id]}
        />
        <div className="flex flex-col flex-1 justify-between w-full pb-6">
          {ANSWER_YESNO_TYPES.includes(currentStepData.type) && (
            <>
              {sortedOptions(currentStepData.quest_question_option).map(
                (option, index) => {
                  const isOptionSelected =
                    currentStepData.type === QuestionType.CHECKBOX
                      ? state.selectedOptions.some(
                          (opt) => opt.id === option.id
                        )
                      : state.selectedOption.id === option.id;

                  return (
                    <AnswerYesNoQuestion
                      key={option.id}
                      letter={index + 1}
                      text={option.option_text}
                      isSelected={isOptionSelected}
                      hasOtherOption={currentStepData?.has_other_option}
                      isOtherOption={option?.is_other_option}
                      onClick={() => handleSelect(option.id, option.is_correct)}
                      onOtherErrorChange={handleOtherErrorChange}
                      onOtherTextChange={handleOtherTextChange}
                      otherText={
                        isOptionSelected && option?.is_other_option
                          ? state.otherOptionTexts[currentStepData.id] || ""
                          : ""
                      }
                      isCheckbox={
                        currentStepData.type === QuestionType.CHECKBOX
                      }
                    />
                  );
                }
              )}
            </>
          )}
          {ANSWER_OPTION_TYPES.includes(currentStepData.type) && (
            <>
              {sortedOptions(currentStepData.quest_question_option).map(
                (option, index) => {
                  const isOptionSelected =
                    currentStepData.type === QuestionType.CHECKBOX
                      ? state.selectedOptions.some(
                          (opt) => opt.id === option.id
                        )
                      : state.selectedOption.id === option.id;

                  return (
                    <AnswerOption
                      key={option.id}
                      letter={index + 1}
                      text={option.option_text}
                      isSelected={isOptionSelected}
                      hasOtherOption={currentStepData?.has_other_option}
                      isOtherOption={option?.is_other_option}
                      onClick={() => handleSelect(option.id, option.is_correct)}
                      onOtherErrorChange={handleOtherErrorChange}
                      onOtherTextChange={handleOtherTextChange}
                      otherText={
                        isOptionSelected && option?.is_other_option
                          ? state.otherOptionTexts[currentStepData.id] || ""
                          : ""
                      }
                      isCheckbox={
                        currentStepData.type === QuestionType.CHECKBOX
                      }
                    />
                  );
                }
              )}
            </>
          )}
          {state.questErrors &&
          currentStepData?.id &&
          state.questErrors[currentStepData.id] ? (
            <div className="flex flex-col items-start mt-4 mb-2 rounded-lg bg-[#F5E3E6] p-2">
              <div className="flex items-center">
                <span className="text-base text-[#EF4056] font-bold">
                  {t("quest.detailMultipleChoice.tip")}:
                </span>
              </div>
              <div className="w-full text-base text-[#EF4056] font-normal">
                {state.questErrors[currentStepData.id].hint}
              </div>
            </div>
          ) : null}

          {ANSWER_TEXT_TYPES.includes(currentStepData.type) && (
            <>
              {currentStepData.type === "long" ? (
                <div className="flex flex-col">
                  <textarea
                    // ref={(textareaRef) => {
                    //   if (textareaRef) {
                    //     setTimeout(() => textareaRef.focus(), 100);
                    //   }
                    // }}
                    value={state.answerText || ""}
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        answerText: e.target.value,
                      }))
                    }
                    minLength={100}
                    className={`min-h-[150px] mt-2 rounded-xl border border-solid focus-visible:outline-none p-2 resize ${
                      state.questErrors[currentStepData.id]
                        ? "border-red-500"
                        : "border-stone-200"
                    }`}
                    placeholder={t("quest.detailMultipleChoice.yourLongAnswer")}
                  />
                  {(state.answerText || "").length > 0 &&
                    (state.answerText || "").length < 100 && (
                      <div className="text-sm text-red-500 mt-1">
                        {t("quest.detailMultipleChoice.minimumLongLengthError")}{" "}
                      </div>
                    )}
                  <div className="text-sm text-gray-500 mt-4 flex justify-end items-end">
                    {(state.answerText || "").length} /{" "}
                    {t("quest.detailMultipleChoice.min100Characters")}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col">
                  <input
                    // ref={(textareaRef) => {
                    //   if (textareaRef) {
                    //     setTimeout(() => textareaRef.focus(), 100);
                    //   }
                    // }}
                    value={state.answerText || ""}
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        answerText: e.target.value,
                      }))
                    }
                    className={`mt-2 h-[50px] rounded-xl border border-solid focus-visible:outline-none p-2 ${
                      state.questErrors[currentStepData.id]
                        ? "border-red-500"
                        : "border-stone-200"
                    }`}
                    placeholder={t(
                      "quest.detailMultipleChoice.yourShortAnswer"
                    )}
                  />

                  {/* {(state.answerText || "").length > 0 &&
                    (state.answerText || "").length < 20 && (
                      <div className="text-sm text-red-500 mt-1">
                        {t(
                          "quest.detailMultipleChoice.minimumShortLengthError"
                        )}{" "}
                      </div>
                    )} */}
                  {/* <div className="text-sm text-gray-500 flex justify-end items-end mt-4">
                    {(state.answerText || "").length} /{" "}
                    {t("quest.detailMultipleChoice.min20Characters")}
                  </div> */}
                </div>
              )}
            </>
          )}
          {ANSWER_SLIDER_TYPES.includes(currentStepData.type) &&
            (() => {
              const sortedOptions = [
                ...(currentStepData.quest_question_option || []),
              ].sort((a, b) => Number(a.value) - Number(b.value));
              const min = 1;
              const max = sortedOptions.length;
              const selectedItem = selectedValue
                ? sortedOptions.find(
                    (item) => Number(item.value) === Number(selectedValue)
                  )
                : null;

              return (
                <div className="mt-2">
                  {!selectedValue && (
                    <div className="text-sm text-[#EF4056] flex justify-start items-end mt-4">
                      {t(
                        "quest.detailMultipleChoice.pleaseSlideToYourPreference"
                      )}
                    </div>
                  )}

                  <div className="text-base font-medium leading-tight text-[#A0A0A0] py-4 mb-8">
                    {min} - {sortedOptions[0]?.option_text}
                    <br />
                    {max} -{" "}
                    {sortedOptions[sortedOptions.length - 1]?.option_text}
                  </div>

                  <div className="flex flex-col items-center mb-4 text-sm text-[#253FF6] font-bold px-2 transition-all duration-150">
                    <span className="text-sm">{selectedItem?.value}</span>
                    <div className="text-xl text-center whitespace-normal break-words max-w-[220px]">
                      {selectedItem?.option_text}
                    </div>
                  </div>

                  <Slider
                    min={min}
                    max={max}
                    step={1}
                    value={selectedValue ? [Number(selectedValue)] : [min]}
                    onValueChange={(value) => {
                      const selectedValue = Math.ceil(value[0]);
                      const selectedItem = sortedOptions.find(
                        (item) => Number(item.value) === selectedValue
                      );
                      if (selectedItem) {
                        handleSelect(selectedItem.id, selectedItem.is_correct);
                      }
                    }}
                  />
                </div>
              );
            })()}

          {currentStepData.is_additional_comment && (
            <div className="flex flex-col mt-6 gap-1 border-t-2">
              <div className="text-xl mt-4 font-medium">
                {t("base.tellUsWhy")}
              </div>
              {/* <div className="text-base font-medium text-[#A0A0A0] mt-1">
                {t("base.elaborateOnYourChoice")}
              </div> */}
              <div
                className={`text-sm font-semibold leading-tight ${
                  state.questErrors[currentStepData.id]
                    ? "text-[#EF4056]"
                    : "text-[#253FF6]"
                }`}
              >
                {}
              </div>
              <textarea
                value={state.additionalComment || ""}
                placeholder={
                  currentStepData?.additional_comment_placeholder || ""
                }
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    additionalComment: e.target.value,
                  }))
                }
                className={`min-h-[150px] mt-2 rounded-xl border border-solid focus-visible:outline-none p-2 w-full ${
                  state.questErrors[currentStepData.id]
                    ? "border-red-500"
                    : "border-stone-200"
                }`}
              />
              {(state.additionalComment || "").length > 0 &&
                (state.additionalComment || "").length < 50 && (
                  <div className="text-sm text-red-500 mt-1">
                    {t("quest.detailMultipleChoice.minimumRemarkLengthError")}{" "}
                  </div>
                )}
              <div className="text-sm text-gray-500 mt-4 flex justify-end items-end">
                {(state.additionalComment || "").length} /{" "}
                {t("quest.detailMultipleChoice.compulsoryRemarks")}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };
  const handleExtendTime = async () => {
    if (!id) {
      toast.error(t("toast.error.invalidParticipantId"));
      return;
    }
    try {
      const response = await triggerTimeExtension({
        quest_participants_id: id,
      }).unwrap();

      if (response.status === 200) {
        // Refetch the quest practice data to get updated expired_at
        const questResponse = await getDetailsPractice({
          participant_id: id,
        }).unwrap();
        if (questResponse.status === 200) {
          const selectedItem = questResponse.data.find(
            (item) => String(item.id) === String(id)
          );
          if (selectedItem) {
            setExpiredAtTime(selectedItem.expired_at || null);
            setQuotaType(
              selectedItem?.quest?.quest_business?.quota_type ?? "SLOT"
            );
          } else {
            console.warn("No matching item found for ID:", id);
            setExpiredAtTime(null);
          }
        }
        timeIsRunningRef.current?.closeModal();
        toast.success(t("toast.success.timeExtensionSuccess"));
      }
    } catch (err) {
      console.error("Failed to extend time:", err);
      if (err.status === 422) {
        toast.error(err.data.detail[0].msg);
        setState((prev) => ({
          ...prev,
          checkRunningTime: true,
        }));
        timeIsRunningRef.current.closeModal();
      } else {
        toast.error(t("toast.error.timeExtensionFailed"));
        setState((prev) => ({
          ...prev,
          checkRunningTime: true,
        }));
        timeIsRunningRef.current.closeModal();
      }
    }
  };

  const renderNextButton = () => {
    const isLastErrorStep =
      state.reviewMode &&
      state.errorStepsArray.length > 0 &&
      state.currentErrorStepIndex === state.errorStepsArray.length - 1;

    const isLastStep = state.currentStep >= totalSteps;

    return (
      <div
        className="flex flex-col justify-end items-center"
        style={{ width: "50px", height: "54px" }}
      >
        {isLastErrorStep || isLastStep ? (
          <>
            <span className="text-sm font-bold text-[#253FF6] whitespace-nowrap">
              {t("base.review")}
            </span>
            <button
              className={cn(
                "mt-1",
                isPostAnswering && "opacity-50 cursor-not-allowed"
              )}
              onClick={() => handleReview()}
              disabled={isPostAnswering}
            >
              <img
                src={images.ArrowRightIconBlue}
                className="w-7 aspect-square"
                alt="Review"
              />
            </button>
          </>
        ) : (
          <button
            className={cn(
              "h-full w-full flex items-end justify-center",
              isPostAnswering && "opacity-50 cursor-not-allowed"
            )}
            onClick={() => handleNextStep()}
            disabled={isPostAnswering}
          >
            <img
              src={images.ArrowRightIconBlue}
              className="w-7 aspect-square"
              alt="Next"
            />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full max-w-[480px] mx-auto bg-[#FFFFFF]">
      <LoadingCommon statusLoading={isTimeRunningLoading || isPostAnswering} />
      <HeaderQuestPratice
        businessAvatar={
          questDetailsData?.data?.quest_business?.business?.avatar_data
            ?.file_path
        }
        quotaType={quotaType}
        thumbnails={thumbnailPaths}
        menuIcon={images.ThreeDotVerticalIcon}
        onMenuClick={() => setIsDropDownOpen(!isDropDownOpen)}
        isDropDownOpen={isDropDownOpen}
        onDropDownPauseClick={() => modalRefs.pause.current?.openModal()}
        onDropDownEndClick={() => modalRefs.end.current?.openModal()}
        onDropReportClick={handleDropReportClick}
      />

      <div className="flex flex-col flex-1 justify-between">
        <div className="flex-1 overflow-y-auto px-3.5 pb-4">
          {renderQuestionContent()}
        </div>

        {state.currentStep !== 1 && (
          <div
            className={cn(
              "fixed bottom-0 left-0 right-0 bg-white z-[19] w-full",
              "max-w-[480px] mx-auto",
              "flex flex-col gap-3.5 px-4 py-3"
            )}
          >
            <div className="flex flex-row justify-between items-end">
              <button
                className={cn(
                  "flex items-end justify-center rounded-xl min-h-[54px] w-[45px]",
                  "transition-all duration-300 ease-in-out",
                  isPrevStepLoading
                    ? "opacity-100 visible cursor-not-allowed"
                    : !state.reviewMode && state.currentStep <= 2
                    ? "opacity-0 invisible pointer-events-none"
                    : "opacity-100 visible"
                )}
                onClick={() => handlePrevStep()}
                disabled={
                  isPrevStepLoading ||
                  (!state.reviewMode && state.currentStep <= 2)
                }
              >
                <img
                  src={images.ArrowRightIconBlue}
                  className="w-7 aspect-square transform -scale-x-100"
                  alt="Previous"
                />
              </button>

              <div className="w-full px-2">
                <ProgressDotGroup
                  currentStep={state.currentStep - 1}
                  totalSteps={totalSteps - 1}
                  errorSteps={state.questErrors[currentStepData?.id]}
                  listAnwserErr={Object.values(state.questErrors || {})}
                  listQuestion={data.data}
                  questAnswers={state.questAnswers}
                  submittedSteps={submittedSteps}
                  onStepChange={handleStepChange}
                  // isReviewed={
                  //   state.reviewMode ||
                  //   Object.keys(state.questErrors).length > 0 ||
                  //   state.isFromReview
                  // }

                  isReviewed={
                    // state.reviewMode ||
                    // Object.keys(state.questErrors).length > 0 ||
                    // SỬA: Giữ isReviewed = true nếu có questAnswers (paused quest)
                    Object.keys(state.questAnswers).length === totalSteps - 1
                  }
                  currentStepData={currentStepData}
                  selectedOption={state.selectedOption}
                  selectedOptions={state.selectedOptions}
                  answerText={state.answerText}
                  additionalComment={state.additionalComment}
                  otherOptionTexts={state.otherOptionTexts}
                  hasOtherError={hasOtherError}
                  maxCheckboxOptions={
                    currentStepData?.minimum_selection_quantity
                  }
                />
              </div>

              {renderNextButton()}
            </div>
          </div>
        )}
      </div>

      <CommonModal
        ref={modalRefs.pause}
        showCloseIcon={false}
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.pause")}
              className={cn(
                "flex-1 px-6 py-3 rounded-md min-h-[40px] text-xl leading-none font-medium  border border-[#253FF6] text-[#253FF6]"
              )}
              onClick={handlePauseTakeQuest}
            />
            <Button
              text={t("base.cancel")}
              className="flex-1 px-6 py-3 rounded-md min-h-[40px] text-xl leading-none font-medium text-white bg-[#253FF6] "
              onClick={() => closeModal(false)}
            />
          </>
        )}
      >
        <div className="flex flex-col items-center w-full gap-2 px-2">
          <img
            src={images.CarbonPauseOutlineBlue}
            alt="Pause quest"
            className="w-6"
          />
          <div className="font-medium text-2xl text-[#253FF6]">
            {t("base.pauseThisQuest")}
          </div>
          <div className="font-medium text-lg text-[#333333] text-center">
            {t("base.pauseQuestConfirmDescription")}
          </div>
          <div className="font-medium text-sm text-[#A0A0A0] text-center">
            {t("base.remainingTime")}
          </div>
          <Timer
            className={"mt-0"}
            time={state.time}
            handleEndQuest={handleEndQuest}
            questId={questId}
            // typeImageOclock={true}
          />
        </div>
      </CommonModal>

      <CommonModal
        ref={modalRefs.end}
        classNameText="font-semibold"
        showCloseIcon={false}
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.quit")}
              className="flex-1 px-6 py-3 rounded-md min-h-[40px] text-xl font-medium  border border-[#253FF6] text-[#253FF6] leading-none"
              onClick={handleEndQuest}
            />
            <Button
              text={t("base.cancel")}
              className="flex-1 px-6 py-3 rounded-md min-h-[40px] text-xl font-medium text-white bg-[#253FF6] leading-none"
              onClick={() => closeModal(false)}
            />
          </>
        )}
      >
        <div className="flex flex-col items-center w-full gap-2 px-2">
          <img
            src={images.CarbonCloseOutlineBlue}
            alt="End quest"
            className="w-6"
          />
          <div className="font-medium text-2xl text-[#253FF6]">
            {t("base.quitThisQuest")}
          </div>
          <div className="font-medium text-lg text-[#333333] text-center">
            {t("base.quitQuestConfirmDescription")}
          </div>
        </div>
      </CommonModal>

      <CommonModal
        ref={modalRefs.submit}
        title={t("quest.detailMultipleChoice.submitQuest")}
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.cancel")}
              className="flex-1 px-5 py-3 rounded-xl min-h-[40px] bg-white border border-stone-200 text-[#A0A0A0]"
              onClick={() => closeModal(false)}
            />
            <Button
              text={t("base.review")}
              className="flex-1 px-5 py-3 rounded-xl min-h-[40px] text-white bg-[#253FF6]"
              variant="primary"
              onClick={handleReview}
            />
          </>
        )}
      >
        <div className="flex flex-col items-center w-full">
          <ProgressBar
            currentStep={state.currentStep}
            totalSteps={totalSteps}
            errorSteps={state.questErrors}
            isReviewed={
              isReviewingDataResponse?.missing_or_not_correct_questions
                ?.length > 0 || Object.keys(state.questErrors).length > 0
            }
            hasReceiptIssue={isReviewingDataResponse?.ocr_receipt_job}
            hasReceiptIssueLocation={isReviewingDataResponse?.is_location}
            hasReceiptIssueDate={isReviewingDataResponse?.is_time}
          />
          <img
            src={images.SubmitQuestImageModal}
            alt="Submit quest"
            className="mt-6 w-[80px] aspect-[1.41]"
          />
        </div>
      </CommonModal>

      <CommonModal
        ref={modalRefs.reviewing}
        canClickOverLay={false}
        showCloseIcon={false}
      >
        <div className="flex flex-col items-center w-full gap-2 px-2">
          <img
            src={images.ReviewingQuestImage}
            alt="End quest"
            className="w-[170px]"
          />
          <div className="font-medium text-2xl text-[#253FF6]">
            {t("base.reviewing")}
          </div>
          <div className="font-medium text-lg text-[#333333] text-center px-1">
            {t(
              "quest.detailMultipleChoice.reviewingYourSubmissionPleaseWaitAMoment"
            )}
          </div>
          <button
            className={`flex-1 shrink gap-2.5 px-5 py-4 rounded-xl w-full text-white text-base font-medium border-none mt-4 ${"bg-[#ECEEFF] cursor-not-allowed"}`}
          >
            <span className="flex items-center justify-center">
              <BeatLoader size={5} loading={true} color="#3534ff" />
            </span>
          </button>
        </div>
      </CommonModal>

      <CommonModal
        ref={modalRefs.reviewComplete}
        showCloseIcon={false}
        classNameText="text-[#201D1B] font-medium text-lg"
      >
        <div className="flex flex-col items-center w-full gap-2 px-2">
          <img
            src={images.ReviewingQuestCompletedImage}
            alt="End quest"
            className="w-[170px]"
          />
          <div className="font-medium text-2xl text-[#253FF6]">
            {t(
              "quest.detailMultipleChoice.improveYourResponsesToUnlockYourRewards"
            )}
          </div>
          {Object.keys(state.questErrors).length > 0 && (
            <div className="font-medium text-lg text-[#333333] text-center">
              {t(
                "quest.detailMultipleChoice.thereAreQuestionsThatCouldUseYourAttention",
                { count: Object.keys(state.questErrors).length }
              )}
            </div>
          )}

          {Object.keys(state.questErrors).length > 0 && (
            <button
              onClick={handleBackQuest}
              className={
                "flex-1 shrink gap-2.5 px-5 py-4 rounded-xl w-full text-white text-base font-medium border-none mt-4 bg-[#253FF6]"
              }
            >
              <span className="flex items-center justify-center">
                {t("base.edit")}
              </span>
            </button>
          )}

          <div className="text-sm font-medium text-[#A0A0A0] text-center mt-4">
            <span>{t("quest.detailMultipleChoice.encounteringIssues")}</span>
            <br />
            <span
              onClick={handleComplete}
              className="border-b border-[#A0A0A0] cursor-pointer"
            >
              {" "}
              {t("quest.detailMultipleChoice.requestForManualReview")}
            </span>
          </div>
        </div>
      </CommonModal>

      <CommonModal
        ref={modalRefs.reviewingComplete}
        title={t("quest.detailMultipleChoice.questCompleted")}
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.retry")}
              className="flex-1 px-5 py-3 rounded-xl min-h-[40px] bg-white border border-stone-200 text-[#A0A0A0]"
              onClick={() => closeModal(false)}
            />
            <Button
              text={t("base.submit")}
              className="flex-1 px-5 py-3 rounded-xl min-h-[40px] text-white bg-[#253FF6]"
              variant="primary"
              onClick={handleComplete}
            />
          </>
        )}
      >
        {congratulation && (
          <div className="flex flex-col items-center w-full">
            <img
              loading="lazy"
              src={images.ReviewCompleteQuestImageModal}
              alt="Quest illustration"
              className="object-contain mt-6 max-w-full aspect-[1.41] w-[80px]"
            />
            <span>
              {t(
                "quest.detailMultipleChoice.congratulationsOnYourNewAchievement"
              )}
            </span>
          </div>
        )}
      </CommonModal>

      <CommonModal
        ref={outQuestModelRef}
        title={t("quest.detailMultipleChoice.timeUp")}
        classNameText="text-[#201D1B] font-medium text-lg "
        canClickOverLay={false}
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.ok")}
              className="flex-1 shrink gap-2.5 self-stretch px-5 py-3 rounded-xl min-h-[40px] text-white bg-[#253FF6]"
              variant="primary"
              onClick={() => {
                closeModal();
                handleSaveQuest();
              }}
            />
          </>
        )}
      >
        <div className="flex justify-center items-center w-full px-4">
          <span className="text-sm font-normal text-[#787A7A] break-words text-wrap text-center">
            {t("quest.detailMultipleChoice.missionHasEndPleaseExitAndTryAgain")}
          </span>
        </div>
      </CommonModal>

      <CommonModal
        ref={timeIsRunningRef}
        classNameText="text-[#201D1B] font-medium text-lg "
        canClickOverLay={false}
        actions={(closeModal) => (
          <>
            <Button
              text={t("quest.detailMultipleChoice.saveAndQuit")}
              className="flex-1 shrink gap-2.5 self-stretch rounded-md min-h-[40px] text-[#253FF6] bg-[#fff] border border-[#253FF6] text-xl font-medium text-center"
              variant="primary"
              onClick={() => {
                closeModal();
                confirmSaveAndQuitRef.current?.openModal();
              }}
            />
            <Button
              text={t("quest.detailMultipleChoice.extend")}
              className="flex-1 shrink gap-2.5 self-stretch rounded-md min-h-[40px] text-white bg-[#253FF6] text-xl font-medium text-center"
              variant="primary"
              onClick={() => {
                handleExtendTime();
              }}
            />
          </>
        )}
      >
        <div className="flex flex-col justify-center items-center w-full gap-2">
          <img src={images.OclockIconRed} alt="" />
          <span className="text-2xl font-medium text-[#EF4056]  text-center">
            {t("quest.detailMultipleChoice.timeIsRunningOut")}
          </span>
          <span className="text-lg font-medium text-[#333333]  text-center">
            {t("quest.detailMultipleChoice.yourTimeIsAlmostUp")}
          </span>
          <div>
            <span className="text-sm text-[#A0A0A0] font-medium">
              {t("base.remainingTime")}
            </span>
            <TimerTypeOclock time={diffSeconds} />
          </div>
        </div>
      </CommonModal>

      <CommonModal
        ref={confirmSaveAndQuitRef}
        classNameText="text-[#201D1B] font-medium text-lg "
        canClickOverLay={false}
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.cancel")}
              className="flex-1 shrink gap-2.5 self-stretch rounded-md min-h-[40px] text-[#253FF6] bg-[#fff] border border-[#253FF6] text-xl font-medium text-center"
              variant="primary"
              onClick={() => {
                closeModal();
              }}
            />
            <Button
              text={t("quest.detailMultipleChoice.saveAndQuit")}
              className="flex-1 shrink gap-2.5 self-stretch rounded-md min-h-[40px] text-white bg-[#253FF6] text-xl font-medium text-center"
              variant="primary"
              onClick={() => {
                closeModal();
                handleSaveQuest();
              }}
            />
          </>
        )}
      >
        <div className="flex flex-col justify-center items-center w-full gap-2">
          <img src={images.SaveIcon} alt="" />
          <span className="text-2xl font-medium text-[#08B89D]  text-center">
            {t("quest.detailMultipleChoice.saveAndQuit")}
          </span>
          <span className="text-lg font-medium text-[#333333]  text-center">
            {t(
              "quest.detailMultipleChoice.yourResponsesWillBeSavedButYourSlotWillBeReleased"
            )}
          </span>
        </div>
      </CommonModal>
    </div>
  );
};
export default DetailMultipleChoice;
